/**
 * src/components/LiveToolsLayer.tsx
 *
 * Visual overlay that renders the broadcaster's currently configured
 * live tools on top of a video element.  Used by both the broadcaster
 * (preview) and viewers (final render).  The layer subscribes to the
 * shared `liveToolsService` event bus so any tool addition / update /
 * value refresh shows up without the parent component re-fetching.
 *
 * Late-joining viewers receive a `tool.snapshot` event from the WS
 * `welcome` payload; until then the layer is empty (no flicker, no
 * spinner — the tools simply pop in once snapshot lands).
 *
 * Broadcasters can pass `mode="broadcaster"` to render an overlay PLUS
 * a small "open control panel" affordance — but the actual control
 * surface is `LiveToolsControlPanel` which is mounted separately
 * inside the broadcaster's HUD so positioning + reflow are at the
 * caller's discretion.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  applyLiveToolEvent,
  dispatchLiveToolEvent,
  getSnapshot as fetchSnapshot,
  subscribeToLiveToolEvents,
  updateTool,
  type LiveTool,
  type LiveToolEvent,
  type LiveToolPosition,
} from '../services/liveToolsService';
import './LiveTools.css';

const ALL_POSITIONS: LiveToolPosition[] = [
  'top_left', 'top_right', 'bottom_left', 'bottom_right',
  'center', 'sidebar_left', 'sidebar_right', 'background',
];

export interface LiveToolsLayerProps {
  /** Paid live-session id.  Required to fetch the initial snapshot. */
  sessionId: string | null | undefined;
  /** Optional backend override — defaults to REACT_APP_BACKEND_URL. */
  backendUrl?: string;
  /**
   * `broadcaster` users see hidden tools too (greyed out) so they
   * know what's queued; `viewer` users only see visible tools.
   */
  mode?: 'broadcaster' | 'viewer';
  /**
   * HMAC grant token for the paid live session — required for drag-
   * and-drop reposition (which fires an authenticated PATCH on the
   * tool).  Optional; if absent drag is disabled and the broadcaster
   * has to use the position selector in the control panel instead.
   */
  grantToken?: string | null;
  /**
   * Local-only minimize state.  Watchers can collapse a card without
   * mutating broadcaster state.  Keyed by tool id.  Optional — if
   * absent the layer renders all tools fully expanded.
   */
  localMinimized?: Record<string, boolean>;
  onLocalToggleMinimized?: (toolId: string) => void;
}

const LiveToolsLayer: React.FC<LiveToolsLayerProps> = ({
  sessionId,
  backendUrl,
  mode = 'viewer',
  grantToken,
  localMinimized,
  onLocalToggleMinimized,
}) => {
  const [tools, setTools] = useState<LiveTool[]>([]);
  // Internal fallback for `localMinimized` so the collapse caret works
  // even when the parent doesn't supply controlled props.  We always
  // prefer the parent's controlled value when present.
  const [internalMinimized, setInternalMinimized] = useState<Record<string, boolean>>({});
  const effectiveMinimized = localMinimized ?? internalMinimized;
  const handleToggleMinimize = (id: string) => {
    if (onLocalToggleMinimized) {
      onLocalToggleMinimized(id);
      return;
    }
    setInternalMinimized((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  // Subscribe to WS events.  The subscriber is process-global so any
  // simultaneous LiveToolsControlPanel + this layer share the same
  // updates.
  useEffect(() => {
    const unsub = subscribeToLiveToolEvents((ev: LiveToolEvent) => {
      if (sessionId && ev.sessionId && ev.sessionId !== sessionId) return;
      setTools((prev) => applyLiveToolEvent(prev, ev));
    });
    return unsub;
  }, [sessionId]);

  // One-shot snapshot fetch so cold renders without a recent WS
  // `tool.snapshot` event still hydrate.  Errors are swallowed — the
  // layer renders empty until WS catches up.
  useEffect(() => {
    if (!sessionId) {
      setTools([]);
      return;
    }
    let cancelled = false;
    fetchSnapshot(sessionId, { backend: backendUrl })
      .then((snap) => {
        if (cancelled) return;
        setTools((prev) => {
          // Don't trample over events that have already arrived since
          // we kicked off the fetch — keep whichever object has the
          // newer `updatedAt`.
          if (!Array.isArray(snap.tools)) return prev;
          if (prev.length === 0) return snap.tools;
          const merged = new Map<string, LiveTool>();
          for (const t of snap.tools) merged.set(t.id, t);
          for (const t of prev) {
            const existing = merged.get(t.id);
            if (!existing || (existing.updatedAt < t.updatedAt)) {
              merged.set(t.id, t);
            }
          }
          return Array.from(merged.values());
        });
      })
      .catch(() => { /* swallow — WS will catch up */ });
    return () => { cancelled = true; };
  }, [sessionId, backendUrl]);

  const grouped = useMemo(() => {
    const slots = new Map<LiveToolPosition, LiveTool[]>();
    for (const p of ALL_POSITIONS) slots.set(p, []);
    for (const t of tools) {
      if (!t.visible && mode !== 'broadcaster') continue;
      const arr = slots.get(t.position) || [];
      arr.push(t);
      slots.set(t.position, arr);
    }
    return slots;
  }, [tools, mode]);

  // Drag-and-drop reposition.  Only enabled in broadcaster mode AND
  // when we have a grant token; viewers never get to drag (their
  // overlay is read-only by design) and broadcasters who haven't paid
  // yet have nothing to authenticate the PATCH with.
  const dragEnabled = mode === 'broadcaster' && !!grantToken;
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [dropTarget, setDropTarget] = useState<LiveToolPosition | null>(null);

  const handleDragStart = (e: React.DragEvent, toolId: string) => {
    if (!dragEnabled) return;
    e.dataTransfer.setData('text/plain', toolId);
    e.dataTransfer.effectAllowed = 'move';
    setDraggingId(toolId);
  };
  const handleDragEnd = () => {
    setDraggingId(null);
    setDropTarget(null);
  };
  const handleDragOver = (e: React.DragEvent, pos: LiveToolPosition) => {
    if (!dragEnabled || !draggingId) return;
    // Allow the drop — without preventDefault the browser won't fire
    // `drop`.  Setting effect=move keeps the UI cursor aligned with
    // the operation we'll actually perform on release.
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dropTarget !== pos) setDropTarget(pos);
  };
  const handleDrop = async (e: React.DragEvent, pos: LiveToolPosition) => {
    if (!dragEnabled) return;
    e.preventDefault();
    const toolId = e.dataTransfer.getData('text/plain') || draggingId;
    setDraggingId(null);
    setDropTarget(null);
    if (!toolId || !sessionId || !grantToken) return;
    const tool = tools.find((t) => t.id === toolId);
    if (!tool || tool.position === pos) return;
    try {
      const updated = await updateTool(
        sessionId, toolId, { position: pos },
        { backend: backendUrl, grantToken },
      );
      dispatchLiveToolEvent({
        event: 'tool.position.updated',
        sessionId,
        tool: updated,
      });
    } catch {
      /* swallow — the panel error surface will get the next failure
         the user actually triggers; a silent drop here is preferable
         to a toast that interrupts the broadcast preview. */
    }
  };

  /**
   * Inline "Show / Hide" toggle handler for the broadcaster's
   * per-card quick-actions row.  We optimistically dispatch a
   * synthetic event so the layer's UI updates without waiting for
   * the WS echo (which can take a beat over a slow link), and the
   * server-authoritative response will re-confirm whatever shipped.
   *
   * Declared BEFORE the `if (!sessionId) return null` early bail so
   * the React Hooks rules-of-order check stays happy across renders
   * where `sessionId` flips from null → defined.
   */
  const handleQuickToggleVisible = useCallback(async (toolId: string, nextVisible: boolean) => {
    if (!sessionId || !grantToken) return;
    try {
      const updated = await updateTool(
        sessionId, toolId, { visible: nextVisible },
        { backend: backendUrl, grantToken },
      );
      dispatchLiveToolEvent({
        event: nextVisible ? 'tool.shown' : 'tool.hidden',
        sessionId,
        tool: updated,
      });
    } catch { /* swallow — the panel surface will retry */ }
  }, [sessionId, grantToken, backendUrl]);

  /** Inline position-picker handler — same optimistic dispatch as
   *  the show/hide toggle above so touch users (no DnD) still get
   *  immediate feedback. */
  const handleQuickSetPosition = useCallback(async (toolId: string, nextPos: LiveToolPosition) => {
    if (!sessionId || !grantToken) return;
    const tool = tools.find((t) => t.id === toolId);
    if (!tool || tool.position === nextPos) return;
    try {
      const updated = await updateTool(
        sessionId, toolId, { position: nextPos },
        { backend: backendUrl, grantToken },
      );
      dispatchLiveToolEvent({
        event: 'tool.position.updated',
        sessionId,
        tool: updated,
      });
    } catch { /* swallow */ }
  }, [sessionId, grantToken, backendUrl, tools]);

  if (!sessionId) return null;

  return (
    <div
      className={
        `live-tools-layer` +
        (draggingId ? ' is-dragging' : '') +
        (mode === 'broadcaster' ? ' mode-broadcaster' : '')
      }
      aria-label="GeoTrust Live Tools"
    >
      {ALL_POSITIONS.map((pos) => {
        const items = grouped.get(pos) || [];
        const isDropTarget = dragEnabled && draggingId && dropTarget === pos;
        // Broadcaster preview keeps every empty corner mounted as a
        // potential drop target / quick-action target.  Watchers
        // skip empty slots entirely so their layer doesn't soak up
        // clicks on the underlying video.
        if (items.length === 0 && mode !== 'broadcaster') return null;
        return (
          <div
            key={pos}
            className={
              `live-tools-slot position-${pos}` +
              (isDropTarget ? ' position-drop-target' : '')
            }
            onDragOver={(e) => handleDragOver(e, pos)}
            onDrop={(e) => handleDrop(e, pos)}
          >
            {items.map((t) => (
              <LiveToolCard
                key={t.id}
                tool={t}
                minimized={!!effectiveMinimized[t.id]}
                onToggleMinimize={() => handleToggleMinimize(t.id)}
                broadcasterView={mode === 'broadcaster'}
                broadcasterCanMutate={mode === 'broadcaster' && !!grantToken}
                draggable={dragEnabled}
                dragging={draggingId === t.id}
                onDragStart={(e) => handleDragStart(e, t.id)}
                onDragEnd={handleDragEnd}
                onQuickToggleVisible={(next) => void handleQuickToggleVisible(t.id, next)}
                onQuickSetPosition={(next) => void handleQuickSetPosition(t.id, next)}
              />
            ))}
          </div>
        );
      })}
    </div>
  );
};

export default LiveToolsLayer;

// ── Card renderer ──────────────────────────────────────────────────────────

interface CardProps {
  tool: LiveTool;
  minimized: boolean;
  onToggleMinimize: () => void;
  broadcasterView: boolean;
  /** True when the broadcaster owns this session AND we have a grant
   *  token capable of mutating its tools.  Drives whether the inline
   *  quick-actions row (Show/Hide pill + position picker) is rendered. */
  broadcasterCanMutate?: boolean;
  draggable?: boolean;
  dragging?: boolean;
  onDragStart?: (e: React.DragEvent) => void;
  onDragEnd?: (e: React.DragEvent) => void;
  onQuickToggleVisible?: (nextVisible: boolean) => void;
  onQuickSetPosition?: (nextPos: LiveToolPosition) => void;
}

const POSITION_LABELS: Record<LiveToolPosition, string> = {
  top_left:      '↖ Top-left',
  top_right:     '↗ Top-right',
  bottom_left:   '↙ Bottom-left',
  bottom_right:  '↘ Bottom-right',
  center:        '◎ Center',
  sidebar_left:  '◧ Sidebar L',
  sidebar_right: '◨ Sidebar R',
  background:    '▥ Background',
};

const LiveToolCard: React.FC<CardProps> = ({
  tool, minimized, onToggleMinimize, broadcasterView,
  broadcasterCanMutate,
  draggable, dragging, onDragStart, onDragEnd,
  onQuickToggleVisible, onQuickSetPosition,
}) => {
  const className = [
    'live-tool-card',
    `display-${tool.displayMode}`,
    !tool.visible && broadcasterView ? 'broadcaster-hidden' : '',
    draggable ? 'broadcaster-draggable' : '',
    dragging ? 'dragging' : '',
  ].filter(Boolean).join(' ');

  return (
    <div
      className={className}
      data-tool-type={tool.type}
      data-tool-id={tool.id}
      draggable={!!draggable}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      title={draggable ? 'Drag to reposition (or use the position picker below)' : undefined}
    >
      <div className="tool-header">
        <span className="tool-title">{tool.displayName}</span>
        <span className={`tool-status ${tool.status}`}>{tool.status}</span>
        <button
          type="button"
          aria-label={minimized ? 'Expand' : 'Minimize'}
          onClick={onToggleMinimize}
          style={{
            background: 'transparent', border: 'none', color: 'inherit',
            cursor: 'pointer', fontSize: 12, padding: 0,
          }}
        >
          {minimized ? '▾' : '▴'}
        </button>
      </div>
      {!minimized && (
        <div className="tool-body-scroll">
          <LiveToolBody tool={tool} />
          {tool.lastUpdatedAt && (
            <div className="tool-updated">
              Updated {timeAgo(tool.lastUpdatedAt)}
            </div>
          )}
        </div>
      )}
      {/* Broadcaster-only inline controls.  Drag-and-drop is the
          fastest way to move a card on desktop, but it doesn't work
          on touch screens — so every card also exposes a position
          dropdown + a Show/Hide pill so phone broadcasters get
          parity.  Hidden when the recorder simply lacks a grant
          token (e.g. payment hasn't cleared yet). */}
      {broadcasterView && broadcasterCanMutate && !minimized && (
        <div
          className="broadcaster-quick-actions"
          /* Stop drag events bubbling so a click on the dropdown or
             the show/hide pill never accidentally starts a drag of
             the card itself. */
          onDragStart={(e) => e.stopPropagation()}
          draggable={false}
        >
          <button
            type="button"
            className={`show-toggle${tool.visible ? '' : ' is-hidden'}`}
            onClick={(e) => {
              e.stopPropagation();
              onQuickToggleVisible?.(!tool.visible);
            }}
            title={tool.visible
              ? 'Hide this card from viewers (you keep seeing it dimmed)'
              : 'Show this card to viewers again'}
          >
            {tool.visible ? 'Visible · click to hide' : 'Hidden · click to show'}
          </button>
          <select
            value={tool.position}
            onChange={(e) => onQuickSetPosition?.(e.target.value as LiveToolPosition)}
            onClick={(e) => e.stopPropagation()}
            title="Move this card to a different corner"
          >
            {(Object.keys(POSITION_LABELS) as LiveToolPosition[]).map((p) => (
              <option key={p} value={p}>{POSITION_LABELS[p]}</option>
            ))}
          </select>
        </div>
      )}
    </div>
  );
};

const LiveToolBody: React.FC<{ tool: LiveTool }> = ({ tool }) => {
  switch (tool.type) {
    case 'price_feed':   return <PriceFeedBody tool={tool} />;
    case 'price_chart':  return <PriceChartBody tool={tool} />;
    case 'contract_function': return <ContractFunctionBody tool={tool} />;
    case 'trustline':    return <TrustlineBody tool={tool} />;
    case 'drone_telemetry':  return <DroneTelemetryBody tool={tool} />;
    case 'gcs_status':       return <GcsStatusBody tool={tool} />;
    case 'airspace_status':  return <AirspaceStatusBody tool={tool} />;
    default: return null;
  }
};

// ── Per-type bodies ────────────────────────────────────────────────────────

const PriceFeedBody: React.FC<{ tool: LiveTool }> = ({ tool }) => {
  const value = tool.lastValue as Array<{ symbol: string; price: string | null; status: string }> | null;
  if (!value || !Array.isArray(value)) {
    return <div className="price-chart-empty">Waiting for price data…</div>;
  }
  const compact = tool.displayMode === 'compact_badge';
  if (compact) {
    return (
      <div className="ticker-row">
        {value.map((v) => (
          <span key={v.symbol} className="ticker">
            <span className="symbol">{v.symbol}</span>
            <span className="price">{v.price ?? '—'}</span>
          </span>
        ))}
      </div>
    );
  }
  return (
    <div>
      {value.map((v) => (
        <div key={v.symbol} className="price-row">
          <span className="symbol">{v.symbol}</span>
          <span className={`price ${v.status !== 'live' ? 'faded' : ''}`}>
            {v.price ?? '—'}
          </span>
        </div>
      ))}
    </div>
  );
};

interface CandleStream {
  symbol: string;
  timeframe: string;
  candles: Array<{ openTime: number; open: string; high: string; low: string; close: string; volume?: string; partial?: boolean }>;
  status: string;
  source?: string;
  note?: string;
  error?: string;
}

const PriceChartBody: React.FC<{ tool: LiveTool }> = ({ tool }) => {
  const stream = tool.lastValue as CandleStream | null;
  if (!stream || !Array.isArray(stream.candles) || stream.candles.length === 0) {
    return <div className="price-chart-empty">No candle data yet.</div>;
  }
  // Minimal SVG candle chart — keeps the bundle small.  A more
  // capable implementation can swap this body for a chart library
  // like lightweight-charts later without touching the data path.
  const w = 360;
  const h = 140;
  const padX = 6;
  const padY = 8;
  const candles = stream.candles.slice(-Math.min(60, stream.candles.length));
  const lows = candles.map((c) => Number(c.low)).filter(Number.isFinite);
  const highs = candles.map((c) => Number(c.high)).filter(Number.isFinite);
  if (lows.length === 0 || highs.length === 0) {
    return <div className="price-chart-empty">Awaiting candle history…</div>;
  }
  const lo = Math.min(...lows);
  const hi = Math.max(...highs);
  const range = hi - lo || hi || 1;
  const cw = (w - padX * 2) / candles.length;
  const yScale = (v: number) => h - padY - ((v - lo) / range) * (h - padY * 2);

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', marginBottom: 4 }}>
        <span style={{ fontSize: 11, opacity: 0.7 }}>{stream.timeframe}</span>
        <span style={{ fontSize: 11, fontWeight: 600 }}>{stream.symbol}</span>
        {stream.note && (
          <span style={{ fontSize: 10, opacity: 0.6, marginLeft: 'auto' }}>
            {stream.note.replaceAll('_', ' ')}
          </span>
        )}
      </div>
      <svg
        className="price-chart-canvas"
        viewBox={`0 0 ${w} ${h}`}
        preserveAspectRatio="none"
        role="img"
        aria-label={`${stream.symbol} ${stream.timeframe} candles`}
      >
        {candles.map((c, i) => {
          const o = Number(c.open);
          const cl = Number(c.close);
          const lh = Number(c.high);
          const ll = Number(c.low);
          if (!Number.isFinite(o) || !Number.isFinite(cl) || !Number.isFinite(lh) || !Number.isFinite(ll)) {
            return null;
          }
          const up = cl >= o;
          const x = padX + i * cw + cw / 2;
          return (
            <g key={i} opacity={c.partial ? 0.55 : 1}>
              <line x1={x} x2={x} y1={yScale(lh)} y2={yScale(ll)}
                    stroke={up ? '#5dffb1' : '#ff7e7e'} strokeWidth={1} />
              <rect
                x={x - cw * 0.35}
                y={Math.min(yScale(o), yScale(cl))}
                width={Math.max(1, cw * 0.7)}
                height={Math.max(1, Math.abs(yScale(o) - yScale(cl)))}
                fill={up ? '#5dffb1' : '#ff7e7e'}
              />
            </g>
          );
        })}
      </svg>
    </div>
  );
};

interface ContractFnPayload {
  contractId: string;
  network: string;
  functionName: string;
  value: unknown;
  formattedValue: string | null;
  status: string;
  error?: string;
}

const ContractFunctionBody: React.FC<{ tool: LiveTool }> = ({ tool }) => {
  const v = tool.lastValue as ContractFnPayload | null;
  if (!v) return <div className="price-chart-empty">Waiting for read result…</div>;
  if (v.status === 'error' || v.status === 'unavailable') {
    return (
      <div>
        <div className="contract-fn-meta">{v.functionName}</div>
        <div className="tool-error-msg">{v.error || v.status}</div>
      </div>
    );
  }
  return (
    <div>
      <div className="contract-fn-meta">
        {v.functionName} · {v.network}
      </div>
      <div className="contract-fn-value">{v.formattedValue ?? '—'}</div>
    </div>
  );
};

interface TrustlinePayload {
  accountId: string;
  mode: string;
  trustlines: Array<{
    assetCode: string; issuer: string | null;
    balance: string; limit: string | null;
    authorized: boolean;
    buyingLiabilities: string;
    sellingLiabilities: string;
  }>;
  status: string;
  error?: string;
}

const TrustlineBody: React.FC<{ tool: LiveTool }> = ({ tool }) => {
  const v = tool.lastValue as TrustlinePayload | null;
  if (!v || !Array.isArray(v.trustlines)) {
    return <div className="price-chart-empty">Waiting for trustline data…</div>;
  }
  if (v.trustlines.length === 0) {
    return (
      <div className="contract-fn-meta">
        No matching trustlines.
        {v.error && <div className="tool-error-msg">{v.error}</div>}
      </div>
    );
  }
  return (
    <div>
      {v.trustlines.map((t, i) => (
        <div key={`${t.assetCode}|${t.issuer || 'native'}|${i}`} className="trustline-row">
          <div>
            <div className="asset">{t.assetCode}</div>
            {t.issuer && <div className="issuer">{t.issuer}</div>}
          </div>
          <div className="balance">
            <div>{t.balance}</div>
            {!t.authorized && <div className="auth-bad">unauthorized</div>}
          </div>
        </div>
      ))}
    </div>
  );
};

// ── Slice 8: drone / GCS / airspace ────────────────────────────────────────

interface DroneTelemetryPayload {
  entityId: string;
  entityType?: string | null;
  displayName?: string | null;
  sysid?: number;
  compid?: number;
  fields?: Record<string, number | boolean | string | null>;
  ageMs?: number | null;
  trustLevel?: string;
  status: string;
  error?: string;
}

const FIELD_LABELS: Record<string, string> = {
  altitude_msl_m:        'Alt MSL',
  altitude_agl_m:        'Alt AGL',
  ground_speed_mps:      'Speed',
  vertical_speed_mps:    'Vert spd',
  heading_deg:           'Heading',
  pitch_deg:             'Pitch',
  roll_deg:              'Roll',
  yaw_deg:               'Yaw',
  battery_remaining_pct: 'Battery',
  voltage_v:             'Volt',
  current_a:             'Amp',
  consumed_mah:          'mAh',
  satellites:            'Sats',
  rssi:                  'RSSI',
  flight_mode:           'Mode',
  armed:                 'Armed',
  lat:                   'Lat',
  lon:                   'Lon',
  home_lat:              'Home lat',
  home_lon:              'Home lon',
};

function _fmtField(key: string, val: number | boolean | string | null | undefined): string {
  if (val == null) return '—';
  if (typeof val === 'boolean') return val ? 'yes' : 'no';
  if (typeof val === 'string') return val;
  if (key.endsWith('_m') || key.endsWith('_mps')) return val.toFixed(1);
  if (key.endsWith('_pct')) return `${Math.round(val)}%`;
  if (key.endsWith('_deg')) return `${val.toFixed(0)}°`;
  if (key === 'voltage_v') return `${val.toFixed(2)}V`;
  if (key === 'current_a') return `${val.toFixed(2)}A`;
  if (key === 'lat' || key === 'lon' || key === 'home_lat' || key === 'home_lon') {
    return val.toFixed(5);
  }
  return String(val);
}

const DroneTelemetryBody: React.FC<{ tool: LiveTool }> = ({ tool }) => {
  const v = tool.lastValue as DroneTelemetryPayload | null;
  if (!v || !v.fields) {
    return <div className="price-chart-empty">Waiting for drone telemetry…</div>;
  }
  const compact = tool.displayMode === 'compact_badge';
  const entries = Object.entries(v.fields);
  if (compact) {
    const summary: string[] = [];
    if (v.fields.altitude_agl_m != null) summary.push(`${(v.fields.altitude_agl_m as number).toFixed(0)}m`);
    if (v.fields.ground_speed_mps != null) summary.push(`${(v.fields.ground_speed_mps as number).toFixed(0)}m/s`);
    if (v.fields.battery_remaining_pct != null) summary.push(`${Math.round(v.fields.battery_remaining_pct as number)}%`);
    if (v.fields.flight_mode != null) summary.push(String(v.fields.flight_mode));
    return (
      <div className="ticker-row" data-trust={v.trustLevel || 'unverified'}>
        {summary.map((s, i) => (
          <span key={i} className="ticker"><span className="price">{s}</span></span>
        ))}
      </div>
    );
  }
  return (
    <div>
      {(v.displayName || v.entityType) && (
        <div style={{ fontSize: 11, opacity: 0.75, marginBottom: 4 }}>
          {v.displayName || v.entityId} · {v.entityType || 'drone'}
          {v.sysid != null ? ` · sysid ${v.sysid}` : ''}
        </div>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2px 12px' }}>
        {entries.map(([k, val]) => (
          <div key={k} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
            <span style={{ opacity: 0.65 }}>{FIELD_LABELS[k] || k}</span>
            <span style={{ fontVariantNumeric: 'tabular-nums' }}>{_fmtField(k, val)}</span>
          </div>
        ))}
      </div>
      {v.error && <div className="tool-error-msg">{v.error}</div>}
    </div>
  );
};

interface GcsStatusPayload {
  gateway: { running: boolean; lastMessageAgeMs: number | null;
    endpoints: { count: number; transports: Record<string, number> } };
  heartbeat: { running: boolean; intervalMs?: number | null; gcsSysid?: number | null };
  pending: { commandAcks: number; ackTimeoutMs?: number | null };
  links: Array<{
    entityId: string; displayName: string | null; entityType: string | null;
    sysid: number | null; compid: number | null;
    linkState: string; ageMs: number | null;
  }>;
  safetyCaps: {
    altitudeCapM: number | null; speedCapMps: number | null;
    distanceCapM: number | null; linkLossTimeoutMs: number | null;
    linkLossRthEnabled: boolean; armedEntities: number | null;
  } | null;
  status: string;
}

const GcsStatusBody: React.FC<{ tool: LiveTool }> = ({ tool }) => {
  const v = tool.lastValue as GcsStatusPayload | null;
  if (!v) {
    return <div className="price-chart-empty">Waiting for GCS status…</div>;
  }
  const compact = tool.displayMode === 'compact_badge';
  const dot = (state: string) => {
    if (state === 'fresh') return '🟢';
    if (state === 'stale') return '🟡';
    if (state === 'lost') return '🔴';
    return '⚪️';
  };
  if (compact) {
    return (
      <div className="ticker-row">
        <span className="ticker">
          <span className="symbol">GCS</span>
          <span className="price">{v.heartbeat.running ? '♥' : '○'}</span>
        </span>
        <span className="ticker">
          <span className="symbol">links</span>
          <span className="price">{v.links.length}</span>
        </span>
        <span className="ticker">
          <span className="symbol">acks</span>
          <span className="price">{v.pending.commandAcks}</span>
        </span>
      </div>
    );
  }
  return (
    <div>
      <div style={{ fontSize: 12, marginBottom: 4 }}>
        Heartbeat: <strong>{v.heartbeat.running ? 'on' : 'off'}</strong>
        {' · '}Pending ACKs: <strong>{v.pending.commandAcks}</strong>
        {' · '}Endpoints: <strong>{v.gateway.endpoints.count}</strong>
      </div>
      {v.links.length > 0 && (
        <div style={{ display: 'grid', gap: 2 }}>
          {v.links.map((l) => (
            <div key={l.entityId} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
              <span>
                {dot(l.linkState)} {l.displayName || l.entityId}
                {l.sysid != null ? ` · sysid ${l.sysid}` : ''}
              </span>
              <span style={{ opacity: 0.7, fontVariantNumeric: 'tabular-nums' }}>
                {l.ageMs != null ? `${(l.ageMs / 1000).toFixed(1)}s` : '—'}
              </span>
            </div>
          ))}
        </div>
      )}
      {v.safetyCaps && (
        <div style={{ fontSize: 11, opacity: 0.7, marginTop: 4 }}>
          Caps: alt {v.safetyCaps.altitudeCapM ?? '—'}m · spd {v.safetyCaps.speedCapMps ?? '—'}m/s · dist {v.safetyCaps.distanceCapM ?? '—'}m
          {v.safetyCaps.linkLossRthEnabled ? ' · RTH on link loss' : ''}
        </div>
      )}
    </div>
  );
};

interface AirspacePayload {
  query: { lat: number; lng: number; altitudeM: number | null; radiusM: number | null };
  classification: string | null;
  controlled: boolean | null;
  maxUasAltitudeFt: number | null;
  facilities: Array<{ name: string | null; type: string | null;
    ceilingFt: number | null; distanceM: number | null }>;
  tfrs: Array<{ name: string | null; ceilingFt: number | null;
    floorFt: number | null; effectiveTo: string | null }>;
  advisories: Array<{ code: string | null; message: string | null }>;
  status: string;
  error?: string;
  errorMessage?: string | null;
}

const AirspaceStatusBody: React.FC<{ tool: LiveTool }> = ({ tool }) => {
  const v = tool.lastValue as AirspacePayload | null;
  if (!v) {
    return <div className="price-chart-empty">Waiting for airspace data…</div>;
  }
  if (v.status === 'error' || v.status === 'unavailable') {
    return (
      <div className="contract-fn-meta">
        <div>Airspace data unavailable.</div>
        {v.errorMessage && <div className="tool-error-msg">{v.errorMessage}</div>}
        <div style={{ fontSize: 10, opacity: 0.65, marginTop: 6, lineHeight: 1.35 }}>
          LAANC is not issued by GeoTrust — use your USS when required.
        </div>
      </div>
    );
  }
  return (
    <div>
      <div style={{ fontSize: 12, marginBottom: 4 }}>
        Class <strong>{v.classification || '—'}</strong>
        {v.controlled != null && <span> · {v.controlled ? 'controlled' : 'uncontrolled'}</span>}
        {v.maxUasAltitudeFt != null && <span> · ceiling {v.maxUasAltitudeFt} ft</span>}
      </div>
      {v.facilities.length > 0 && (
        <div style={{ display: 'grid', gap: 2, marginBottom: 4 }}>
          <div style={{ fontSize: 11, opacity: 0.7 }}>Facilities</div>
          {v.facilities.slice(0, 6).map((f, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
              <span>{f.name || f.type || '—'}</span>
              <span style={{ opacity: 0.7 }}>
                {f.distanceM != null ? `${(f.distanceM / 1000).toFixed(1)}km` : ''}
                {f.ceilingFt != null ? ` · ${f.ceilingFt}ft` : ''}
              </span>
            </div>
          ))}
        </div>
      )}
      {v.tfrs.length > 0 && (
        <div style={{ display: 'grid', gap: 2, marginBottom: 4 }}>
          <div style={{ fontSize: 11, opacity: 0.7, color: '#d33' }}>Active TFRs</div>
          {v.tfrs.slice(0, 4).map((t, i) => (
            <div key={i} style={{ fontSize: 12 }}>
              {t.name || 'TFR'}{t.effectiveTo ? ` (until ${t.effectiveTo})` : ''}
            </div>
          ))}
        </div>
      )}
      {v.advisories.length > 0 && (
        <div style={{ fontSize: 11, opacity: 0.85 }}>
          {v.advisories.slice(0, 3).map((a, i) => (
            <div key={i}>• {a.message || a.code}</div>
          ))}
        </div>
      )}
      <div style={{ fontSize: 10, opacity: 0.65, marginTop: 6, lineHeight: 1.35 }}>
        Informational snapshot from GeoLink when configured. LAANC authorization is not issued by GeoTrust — use your USS / LAANC
        workflow when required (Part 107 / recreational rules still apply).
      </div>
    </div>
  );
};

// ── helpers ────────────────────────────────────────────────────────────────

function timeAgo(iso: string): string {
  try {
    const ms = Date.now() - new Date(iso).getTime();
    if (ms < 5_000) return 'just now';
    if (ms < 60_000) return `${Math.floor(ms / 1000)}s ago`;
    if (ms < 3_600_000) return `${Math.floor(ms / 60_000)}m ago`;
    return `${Math.floor(ms / 3_600_000)}h ago`;
  } catch {
    return '';
  }
}
